import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Upload, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";
import { scAmcMe, scAmcUploadList, scAmcUploadDetail } from "../../lib/sifcontrolApi";

const STATUS_STYLES = {
    draft: { cls: "bg-oth-bg-2 text-oth-ink3", label: "Draft" },
    validated: { cls: "bg-blue-100 text-blue-800", label: "Validated" },
    pending_admin: { cls: "bg-yellow-100 text-yellow-800", label: "Pending review" },
    approved: { cls: "bg-green-100 text-green-800", label: "Approved" },
    rejected: { cls: "bg-red-100 text-red-800", label: "Rejected" },
};

const StatusPill = ({ status }) => {
    const s = STATUS_STYLES[status] || { cls: "bg-oth-bg-2 text-oth-ink3", label: status || "—" };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-body uppercase tracking-wider ${s.cls}`}>{s.label}</span>;
};

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
};

export default function SifControlAmcUploads() {
    const [me, setMe] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { id } = useParams();

    const load = async () => {
        setLoading(true);
        try {
            const r = await scAmcUploadList();
            setItems(r.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load uploads");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        scAmcMe().then(setMe).catch(() => navigate("/sifcontrol/amc/login"));
        load();
    }, [navigate]);

    if (!me) return <div className="p-16 text-center text-sm text-oth-ink3">Loading…</div>;

    if (id) return <UploadDetail id={id} onBack={() => navigate("/sifcontrol/amc/uploads")} />;

    return (
        <section className="py-14 md:py-20" data-testid="sc-amc-uploads-page">
            <div className="mx-auto max-w-[1080px] px-4 md:px-6">
                <button onClick={() => navigate("/sifcontrol/amc/dashboard")} className="text-xs font-body text-oth-ink3 hover:text-oth-ink inline-flex items-center gap-1 mb-4" data-testid="sc-amc-uploads-back">
                    <ArrowLeft size={12} /> Back to dashboard
                </button>
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body">Uploads</div>
                        <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight text-oth-ink">Upload history</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} className="btn-pill !text-xs !py-1.5 !px-3" data-testid="sc-amc-uploads-refresh">
                            <RefreshCcw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                        </button>
                        <Link to="/sifcontrol/amc/upload" className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-amc-uploads-new">
                            <Upload size={12} /> New upload
                        </Link>
                    </div>
                </div>

                <div className="mt-8 rounded-3xl border border-oth-line bg-white overflow-hidden">
                    {loading ? (
                        <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-10 text-center text-sm font-body text-oth-ink3" data-testid="sc-amc-uploads-empty">
                            No uploads yet. Download the template and submit your first update.
                        </div>
                    ) : (
                        <table className="w-full text-[12px] font-body">
                            <thead className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body bg-oth-bg-2/60">
                                <tr>
                                    <th className="text-left px-5 py-3">File</th>
                                    <th className="text-left px-5 py-3">Created</th>
                                    <th className="text-left px-5 py-3">Status</th>
                                    <th className="text-left px-5 py-3">Rows</th>
                                    <th className="text-left px-5 py-3">Applied</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((u) => (
                                    <tr key={u.id} className="border-t border-oth-line/70" data-testid={`sc-amc-upload-row-${u.id}`}>
                                        <td className="px-5 py-3 text-oth-ink truncate max-w-[240px]">{u.filename}</td>
                                        <td className="px-5 py-3 text-oth-ink3">{fmt(u.created_at)}</td>
                                        <td className="px-5 py-3"><StatusPill status={u.status} /></td>
                                        <td className="px-5 py-3 text-oth-ink3">{u.preview?.summary?.total ?? "—"}</td>
                                        <td className="px-5 py-3 text-oth-ink3">{u.status === "approved" ? u.applied_count : "—"}</td>
                                        <td className="px-5 py-3 text-right">
                                            <Link to={`/sifcontrol/amc/uploads/${u.id}`} className="text-[11px] underline hover:text-oth-ink" data-testid={`sc-amc-upload-detail-link-${u.id}`}>View</Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </section>
    );
}

function UploadDetail({ id, onBack }) {
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        scAmcUploadDetail(id).then(setDoc).catch(() => setDoc(null)).finally(() => setLoading(false));
    }, [id]);
    if (loading) return <div className="p-16 text-center text-sm text-oth-ink3">Loading…</div>;
    if (!doc) return <div className="p-16 text-center text-sm text-oth-ink3">Not found.</div>;
    const summary = doc.preview?.summary || {};
    const rows = doc.preview?.rows || [];
    return (
        <section className="py-14 md:py-20" data-testid="sc-amc-upload-detail">
            <div className="mx-auto max-w-[1080px] px-4 md:px-6">
                <button onClick={onBack} className="text-xs font-body text-oth-ink3 hover:text-oth-ink inline-flex items-center gap-1 mb-4">
                    <ArrowLeft size={12} /> All uploads
                </button>
                <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body">Upload</div>
                <h1 className="mt-1 font-display text-2xl md:text-3xl font-semibold tracking-tight text-oth-ink">{doc.filename}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] font-body text-oth-ink3">
                    <StatusPill status={doc.status} />
                    <span><Clock size={11} className="inline mr-1" /> Created {fmt(doc.created_at)}</span>
                    {doc.submitted_at && <span>Submitted {fmt(doc.submitted_at)}</span>}
                    {doc.reviewed_at && <span>Reviewed {fmt(doc.reviewed_at)} by {doc.reviewer_email || "admin"}</span>}
                </div>

                {doc.review_note && (
                    <div className={`mt-4 rounded-xl border px-4 py-3 text-[13px] font-body ${doc.status === "rejected" ? "bg-red-50 border-red-200 text-red-700" : "bg-oth-bg-2/60 border-oth-line text-oth-ink2"}`} data-testid="sc-amc-upload-review-note">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 mb-1">Reviewer note</div>
                        {doc.review_note}
                    </div>
                )}

                <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                        { label: "Rows", value: summary.total ?? "—" },
                        { label: "Ready", value: (summary.ok ?? 0) + (summary.warn ?? 0) },
                        { label: "Unchanged", value: summary.unchanged ?? 0 },
                        { label: "Errors", value: summary.error ?? 0 },
                        { label: "Applied", value: doc.applied_count ?? 0 },
                    ].map((t) => (
                        <div key={t.label} className="rounded-2xl border border-oth-line bg-white p-4">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">{t.label}</div>
                            <div className="mt-1 font-display text-2xl font-semibold">{t.value}</div>
                        </div>
                    ))}
                </div>

                {rows.length > 0 && (
                    <div className="mt-6 rounded-3xl border border-oth-line bg-white overflow-hidden">
                        <div className="px-5 py-3 border-b border-oth-line/70 text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">Rows</div>
                        <div className="max-h-[520px] overflow-auto thin-scroll">
                            <table className="w-full text-[12px] font-body">
                                <thead className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 bg-oth-bg-2/60">
                                    <tr>
                                        <th className="text-left px-4 py-2">SIF</th>
                                        <th className="text-left px-4 py-2">Status</th>
                                        <th className="text-left px-4 py-2">Changes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={`${r.row_number}-${r.sif_code}`} className="border-t border-oth-line/70 align-top">
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-oth-ink">{r.sif_code || "—"}</div>
                                                <div className="text-[11px] text-oth-ink3">{r.sif_name || ""}</div>
                                            </td>
                                            <td className="px-4 py-3">{r.status}</td>
                                            <td className="px-4 py-3">
                                                {r.errors?.map((e, i) => (<div key={`e-${i}`} className="text-red-700 flex items-start gap-1"><XCircle size={12} className="mt-0.5 shrink-0" />{e}</div>))}
                                                {r.warnings?.map((w, i) => (<div key={`w-${i}`} className="text-yellow-800 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{w}</div>))}
                                                {r.changes?.map((c, i) => (
                                                    <div key={`c-${i}`} className="text-oth-ink2">
                                                        <span className="text-oth-ink3">{c.field}:</span>{" "}
                                                        <span className="line-through text-oth-ink3">{String(c.old || "—")}</span>{" → "}
                                                        <span className={doc.status === "approved" ? "text-green-800" : "text-oth-ink"}>{String(c.new || "—")}</span>
                                                    </div>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {doc.status === "approved" && (
                    <div className="mt-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-[13px] font-body px-4 py-3 inline-flex items-center gap-2">
                        <CheckCircle2 size={14} /> Applied {doc.applied_count} row{doc.applied_count === 1 ? "" : "s"} to your SIF masters.
                    </div>
                )}
            </div>
        </section>
    );
}
